import { z } from "zod"

/** Roles are modes, not seats: one person may hold several across workspaces; one per membership. */
export const Role = z.enum(["admin", "marketing", "support_manager", "agent"])
export type Role = z.infer<typeof Role>

export const ROLE_LABELS: Record<Role, string> = {
  admin: "Admin",
  marketing: "Marketing",
  support_manager: "Support manager",
  agent: "Agent",
}

export const ROLE_SUMMARY: Record<Role, string> = {
  admin: "Full access to the workspace and customer data.",
  marketing: "Create and publish automations and campaigns.",
  support_manager: "Manage support assignments and moderation.",
  agent: "Reply, assign and resolve conversations.",
}

export const Permission = z.enum([
  "read",
  "reply",
  "manage_support",
  "edit_automations",
  "publish",
  "manage_workspace",
  "manage_team",
  "manage_customer_data",
])
export type Permission = z.infer<typeof Permission>

/** The matrix from the Roles & permissions screen. This is the single source of truth for API guards and UI gating. */
export const PERMISSION_MATRIX: ReadonlyArray<{ permission: Permission; label: string; roles: readonly Role[] }> = [
  { permission: "read", label: "Read conversations and contacts", roles: ["admin", "marketing", "support_manager", "agent"] },
  { permission: "reply", label: "Reply, assign and resolve", roles: ["admin", "support_manager", "agent"] },
  { permission: "manage_support", label: "Manage assignments and moderation", roles: ["admin", "support_manager"] },
  { permission: "edit_automations", label: "Edit automations and campaigns", roles: ["admin", "marketing"] },
  { permission: "publish", label: "Publish flows and send campaigns", roles: ["admin", "marketing"] },
  { permission: "manage_workspace", label: "Manage channels and workspace", roles: ["admin"] },
  { permission: "manage_team", label: "Invite teammates and assign roles", roles: ["admin"] },
  { permission: "manage_customer_data", label: "Export or delete customer data", roles: ["admin"] },
]

export function can(role: Role, permission: Permission): boolean {
  const row = PERMISSION_MATRIX.find((r) => r.permission === permission)
  return row ? row.roles.includes(role) : false
}
